import React, { useEffect } from "react";
import { Hook, IMutator } from "sonobello.utilities.react";

import { DisqualifyingReason } from "../../types/DisqualifyingReason";
import { ISomethingWentWrongRouter } from "../../routing/types/IRouter";
import { IDisqualifyingFlow } from "../../Flows/types/IFlow";

interface IDisqualifyingReasonsLoaderProps extends IMutator<DisqualifyingReason[]> {
  /** The contact ID for fetching disqualifying reasons. */
  contactId: string;
  /** Hook that fetches the disqualifying reasons. */
  useGetDisqualifyingReasons: Hook<DisqualifyingReason[], boolean, { contactId: string; disabled?: boolean }>;
  /** Router for error handling. */
  router: ISomethingWentWrongRouter;
  /** Interface identifying if the current flow can possibly disqualify the user. */
  flow: IDisqualifyingFlow;
}

export type { IDisqualifyingReasonsLoaderProps };

/** Component that loads disqualifying reasons and updates the application state.
 * Only fetches disqualifying reasons if the current flow requires them.
 * Handles errors by routing to the something went wrong view.
 */
const DisqualifyingReasonsLoader: React.FC<IDisqualifyingReasonsLoaderProps> = ({
  contactId,
  useGetDisqualifyingReasons: useGetReasons,
  router,
  onChange,
  flow
}) => {
  const flowNeedsDisqualifyingReasons = flow.isDisqualificationPossible();
  const { result, error } = useGetReasons({ contactId, disabled: !flowNeedsDisqualifyingReasons });

  useEffect(() => {
    if (result) {
      onChange(result);
    }
  }, [result, onChange]);

  useEffect(() => {
    if (error) {
      router.goToSomethingWentWrongView();
    }
  }, [error, router]);

  useEffect(() => {
    // Clear disqualifying reasons if flow no longer needs them
    if (!flowNeedsDisqualifyingReasons) {
      onChange([]);
    }
  }, [flowNeedsDisqualifyingReasons, onChange]);

  return <React.Fragment />;
};

export default DisqualifyingReasonsLoader;
